import { WorkflowGraphEdge, WorkflowGraphNode, WorkflowGraph } from 'cio/utils/visual-workflow-builder/workflow-graph';
import { LAYER_HEIGHT, NODE_SEP } from 'cio/utils/visual-workflow-builder/layout/size';

// When checking collisions, we're allowing less space than NODE_SEP between
// items. This is because the algorithm seems to produce tighter results when
// multiple passes are needed, and allowing for less space reduces the number of
// loops required to reach a solution
const CHECK_COLLISIONS_LEEWAY = 15;

export function mapOutgoingEdges(edges: WorkflowGraphEdge[]) {
  return edges.reduce(
    (acc, edge) => {
      if (!acc[edge.from]) {
        acc[edge.from] = [];
      }

      acc[edge.from].push(edge);
      return acc;
    },
    {} as Record<string, WorkflowGraphEdge[]>,
  );
}

export function mapIncomingEdges(edges: WorkflowGraphEdge[]) {
  return edges.reduce(
    (acc, edge) => {
      if (!acc[edge.to]) {
        acc[edge.to] = [];
      }

      acc[edge.to].push(edge);
      return acc;
    },
    {} as Record<string, WorkflowGraphEdge[]>,
  );
}

export function layout(unitNodesGraph: WorkflowGraph) {
  let originalOutgoingEdgesMap = unitNodesGraph.outEdges;
  let originalIncomingEdgesMap = unitNodesGraph.inEdges;

  Object.keys(originalOutgoingEdgesMap).forEach((key) => {
    originalOutgoingEdgesMap[key] = originalOutgoingEdgesMap[key].sort(
      (a, b) => unitNodesGraph.traversalOrder.indexOf(a.to) - unitNodesGraph.traversalOrder.indexOf(b.to),
    );
  });
  Object.keys(originalIncomingEdgesMap).forEach((key) => {
    originalIncomingEdgesMap[key] = originalIncomingEdgesMap[key].sort(
      (a, b) => unitNodesGraph.traversalOrder.indexOf(a.from) - unitNodesGraph.traversalOrder.indexOf(b.from),
    );
  });

  let outgoingEdgesMap = originalOutgoingEdgesMap;
  let incomingEdgesMap = originalIncomingEdgesMap;

  let exitNodeIds = Object.values(unitNodesGraph.nodes)
    .filter(
      (node) =>
        node.type === 'exit_action' ||
        (node.type === 'virtual_unit' && node.originalNode?.type === 'exit_action' && node.isLastIndex) ||
        (node.type === 'virtual_dummy' &&
          node.originalNode?.type === 'virtual_unit' &&
          node.originalNode?.originalNode?.type === 'exit_action' &&
          node.isLastIndex),
    )
    .mapBy('id');

  let edgesWithoutExitConnections = Object.values(unitNodesGraph.edges).filter(
    (edge) => !exitNodeIds.includes(edge.from),
  );
  let outgoingEdgesMapWithoutExits = mapOutgoingEdges(edgesWithoutExitConnections);
  let incomingEdgesMapWithoutExits = mapIncomingEdges(edgesWithoutExitConnections);

  Object.keys(outgoingEdgesMapWithoutExits).forEach((key) => {
    outgoingEdgesMapWithoutExits[key] = outgoingEdgesMapWithoutExits[key].sort(
      (a, b) => unitNodesGraph.traversalOrder.indexOf(a.to) - unitNodesGraph.traversalOrder.indexOf(b.to),
    );
  });
  Object.keys(incomingEdgesMapWithoutExits).forEach((key) => {
    incomingEdgesMapWithoutExits[key] = incomingEdgesMapWithoutExits[key].sort(
      (a, b) => unitNodesGraph.traversalOrder.indexOf(a.from) - unitNodesGraph.traversalOrder.indexOf(b.from),
    );
  });

  function assignCoordinates() {
    let layers = [...Object.entries(unitNodesGraph.layeredRanks)]
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map((ranks) => ranks[1].map((id) => unitNodesGraph.nodes[id]));

    layers.forEach((layer, i) => {
      layer.forEach((node) => {
        node.y = i * LAYER_HEIGHT;
      });
    });

    let reversedLayers = [...layers].reverse();
    // FROM LEFT
    layers.forEach((layer) => {
      initialAssignmentLeft(layer);
    });

    let hadCollisions = true;
    let loops = 0;
    while (hadCollisions && loops < 10) {
      hadCollisions = false;
      reversedLayers.slice(0, reversedLayers.length - 1).forEach((bottomLayer, i) => {
        let topLayer = reversedLayers[i + 1];
        centerFromBottom(bottomLayer, topLayer, true, false, false, true);
      });
      layers.slice(0, layers.length - 1).forEach((topLayer, i) => {
        let bottomLayer = layers[i + 1];
        centerFromTop(topLayer, bottomLayer, false, true, false, true);
      });
      reversedLayers.slice(0, reversedLayers.length - 1).forEach((bottomLayer, i) => {
        let topLayer = reversedLayers[i + 1];
        centerFromBottom(bottomLayer, topLayer, true, true, false, true);
      });
      layers.slice(0, layers.length - 1).forEach((topLayer, i) => {
        let bottomLayer = layers[i + 1];
        centerFromTop(topLayer, bottomLayer, false, false, false, true);
      });

      outgoingEdgesMap = outgoingEdgesMapWithoutExits;
      incomingEdgesMap = incomingEdgesMapWithoutExits;
      layers.slice(0, layers.length - 1).forEach((topLayer, i) => {
        let bottomLayer = layers[i + 1];
        centerFromTop(topLayer, bottomLayer, false, false, false, true);
      });
      outgoingEdgesMap = originalOutgoingEdgesMap;
      incomingEdgesMap = originalIncomingEdgesMap;

      hadCollisions = layers.some((layer) => {
        return layer.some((node) => checkCollisions(node, layer));
      });

      loops++;
    }
  }

  function checkCollisions(node: WorkflowGraphNode, layer: WorkflowGraphNode[]) {
    let hasCollisions = false;

    let nodesBefore = layer.slice(0, layer.indexOf(node)).reverse();
    let nodesAfter = layer.slice(layer.indexOf(node) + 1);

    hasCollisions = nodesBefore.some((currentNode, i) => {
      let previousNode = i === 0 ? node : nodesBefore[i - 1];

      return (
        currentNode.x + currentNode.width / 2 >
        previousNode.x - previousNode.width / 2 - NODE_SEP + CHECK_COLLISIONS_LEEWAY
      );
    });

    if (hasCollisions) {
      return hasCollisions;
    }

    hasCollisions = nodesAfter.some((currentNode, i) => {
      let previousNode = i === 0 ? node : nodesAfter[i - 1];

      return (
        currentNode.x - currentNode.width / 2 <
        previousNode.x + previousNode.width / 2 + NODE_SEP - CHECK_COLLISIONS_LEEWAY
      );
    });

    return hasCollisions;
  }

  function fixCollisions(node: WorkflowGraphNode, layer: WorkflowGraphNode[]) {
    let nodesBefore = layer.slice(0, layer.indexOf(node));
    let nodesAfter = layer.slice(layer.indexOf(node) + 1);

    [...nodesBefore].reverse().reduce((previousNode, currentNode) => {
      if (currentNode.x + currentNode.width / 2 > previousNode.x - previousNode.width / 2 - NODE_SEP) {
        currentNode.x = Math.floor(previousNode.x - previousNode.width / 2 - NODE_SEP - currentNode.width / 2);
      }
      return currentNode;
    }, node);

    nodesAfter.reduce((previousNode, currentNode) => {
      if (currentNode.x - currentNode.width / 2 < previousNode.x + previousNode.width / 2 + NODE_SEP) {
        currentNode.x = Math.floor(previousNode.x + previousNode.width / 2 + NODE_SEP + currentNode.width / 2);
      }
      return currentNode;
    }, node);
  }

  function initialAssignmentLeft(layer: WorkflowGraphNode[]) {
    let [firstNode] = layer;
    firstNode.x = Math.floor(firstNode.width / 2);

    layer.forEach((node) => fixCollisions(node, layer));
  }

  function centerFromTop(
    topLayer: WorkflowGraphNode[],
    bottomLayer: WorkflowGraphNode[],
    shouldAdjust = false,
    shouldFixCollisions = false,
    reverse = false,
    shouldAdjustMultipleChildren = false,
  ) {
    if (reverse) {
      topLayer = [...topLayer].reverse();
      bottomLayer = [...bottomLayer].reverse();
    }

    topLayer.forEach((topNode) => {
      let childNodes = (outgoingEdgesMap[topNode.id] ?? [])
        .map((edge) => edge.to)
        .uniq()
        .map((id) => unitNodesGraph.nodes[id]);

      if (reverse) {
        childNodes = [...childNodes].reverse();
      }

      if (childNodes.length === 1) {
        let [childNode] = childNodes;
        if (bottomLayer.includes(childNode)) {
          let parentNodes = (incomingEdgesMap[childNode.id] ?? [])
            .map((edge) => edge.from)
            .uniq()
            .map((id) => unitNodesGraph.nodes[id]);

          if (reverse) {
            parentNodes = [...parentNodes].reverse();
          }

          let oldX = childNode.x;

          if (parentNodes.length > 1) {
            let [firstParent] = parentNodes;
            let lastParent = parentNodes[parentNodes.length - 1];
            childNode.x = Math.floor((firstParent.x + lastParent.x) / 2);
          } else {
            childNode.x = Math.floor(topNode.x);
          }

          let diff = Math.floor(childNode.x - oldX);

          if (diff !== 0 && shouldAdjust) {
            let nodesBefore = bottomLayer
              .slice(0, bottomLayer.indexOf(childNode))
              .filter(
                (n) =>
                  (n.type === 'virtual_dummy' || n.type === 'virtual_unit') &&
                  n.originalNode &&
                  n.originalNode?.type === 'exit_action',
              );
            let nodesAfter = bottomLayer.slice(bottomLayer.indexOf(childNode) + 1);
            if (reverse) {
              [nodesBefore, nodesAfter] = [nodesAfter, nodesBefore];
            }
            nodesBefore.forEach((bn) => (bn.x += diff));
            nodesAfter.forEach((an) => (an.x += diff));
          }
          if (shouldFixCollisions) {
            if (reverse) {
              fixCollisions(childNode, [...bottomLayer].reverse());
            } else {
              fixCollisions(childNode, bottomLayer);
            }
          }
        }
      } else if (shouldAdjustMultipleChildren && childNodes.length > 1) {
        let diff = 0;

        if (childNodes.length % 2 === 0) {
          let [firstNode] = childNodes;
          let lastNode = childNodes[childNodes.length - 1];
          let childNodesMean = (firstNode.x + lastNode.x) / 2;
          diff = Math.floor(topNode.x - childNodesMean);
        } else {
          let centerNode = childNodes[Math.floor(childNodes.length / 2)];
          diff = Math.floor(topNode.x - centerNode.x);
        }

        if (diff !== 0) {
          childNodes.forEach((node) => {
            node.x += diff;
          });
        }
        if (shouldFixCollisions) {
          if (childNodes.length % 2 === 0) {
            let centerLeftIndex = childNodes.length / 2 - 1;

            fixCollisions(childNodes[centerLeftIndex], bottomLayer);
          } else {
            let centerIndex = Math.floor(childNodes.length / 2);
            let centerChild = childNodes[centerIndex];

            fixCollisions(centerChild, bottomLayer);
          }
        }
      }
    });
  }

  function centerFromBottom(
    bottomLayer: WorkflowGraphNode[],
    topLayer: WorkflowGraphNode[],
    shouldAdjust = false,
    shouldFixCollisions = false,
    reverse = false,
    shouldAdjustMultipleParents = false,
  ) {
    if (reverse) {
      bottomLayer = [...bottomLayer].reverse();
      topLayer = [...topLayer].reverse();
    }
    bottomLayer.forEach((bottomNode) => {
      let parentNodes = (incomingEdgesMap[bottomNode.id] ?? [])
        .map((edge) => edge.from)
        .uniq()
        .map((id) => unitNodesGraph.nodes[id]);

      if (reverse) {
        parentNodes = [...parentNodes].reverse();
      }

      if (parentNodes.length === 1) {
        let [parentNode] = parentNodes;
        if (topLayer.includes(parentNode)) {
          let parentChildren = (outgoingEdgesMap[parentNode.id] ?? [])
            .map((edge) => edge.to)
            .uniq()
            .map((id) => unitNodesGraph.nodes[id]);

          if (reverse) {
            parentChildren = [...parentChildren].reverse();
          }

          let oldX = parentNode.x;

          if (parentChildren.length > 1) {
            if (parentChildren.length % 2 === 0) {
              let [firstNode] = parentChildren;
              let lastNode = parentChildren[parentChildren.length - 1];
              let childNodesMean = (firstNode.x + lastNode.x) / 2;

              parentNode.x = Math.floor(childNodesMean);
            } else {
              let centerChild = parentChildren[Math.floor(parentChildren.length / 2)];
              parentNode.x = Math.floor(centerChild.x);
            }
          } else {
            parentNode.x = Math.floor(bottomNode.x);
          }

          let diff = Math.floor(parentNode.x - oldX);

          if (diff !== 0 && shouldAdjust) {
            let nodesBefore = topLayer
              .slice(0, topLayer.indexOf(parentNode))
              .filter(
                (n) =>
                  (n.type === 'virtual_dummy' || n.type === 'virtual_unit') &&
                  n.originalNode &&
                  n.originalNode?.type === 'exit_action',
              );
            let nodesAfter = topLayer.slice(topLayer.indexOf(parentNode) + 1);
            if (reverse) {
              [nodesBefore, nodesAfter] = [nodesAfter, nodesBefore];
            }
            nodesBefore.forEach((bn) => (bn.x += diff));
            nodesAfter.forEach((an) => (an.x += diff));
          }
          if (shouldFixCollisions) {
            if (reverse) {
              fixCollisions(parentNode, [...topLayer].reverse());
            } else {
              fixCollisions(parentNode, topLayer);
            }
          }
        }
      } else if (shouldAdjustMultipleParents && parentNodes.length > 1) {
        let diff = 0;
        if (parentNodes.length % 2 === 0) {
          let [firstNode] = parentNodes;
          let lastNode = parentNodes[parentNodes.length - 1];
          let parentNodesMean = (firstNode.x + lastNode.x) / 2;
          diff = Math.floor(bottomNode.x - parentNodesMean);
        } else {
          let centerNode = parentNodes[Math.floor(parentNodes.length / 2)];
          diff = Math.floor(bottomNode.x - centerNode.x);
        }

        if (diff !== 0) {
          parentNodes.forEach((node) => {
            node.x += diff;
          });
        }
      }
    });
  }

  assignCoordinates();
}
